import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2 } from 'lucide-react';
import './ClinicalMapper.css';
import auditService from './services/auditService';
import { useRoomTypes } from './hooks/useNomenclatures';
import RiskClassificationAssignmentPanel from './components/riskClassification/RiskClassificationAssignmentPanel';
import { useOrg } from './hooks/useOrg';

const ClinicalMapper = ({ structure, setStructure, selectedItem, setSelectedItem, activeLevel, goToLists, userId }) => {
  const { t } = useTranslation('clinical');
  const { activeOrgId: organizationId, isLoading: orgLoading } = useOrg();
  const [expandedItems, setExpandedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Track newly added item so Cancel can remove it if user never saved
  const [pendingNewItem, setPendingNewItem] = useState(null); // { type, id }
  
  // Load room types from nomenclature
  const { items: roomTypes, loading: roomTypesLoading } = useRoomTypes(true);
  
  // Debug: Log room types when they load
  useEffect(() => {
    if (!roomTypesLoading) {
      console.log('🚪 Room Types loaded:', roomTypes);
      console.log('🚪 Room Types count:', roomTypes?.length);
    }
  }, [roomTypes, roomTypesLoading]);

  const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

  // Get current entity type from activeLevel
  const getCurrentEntityType = () => {
    switch(activeLevel) {
      case 'admin-hospitals': return 'organizations';
      case 'admin-locations': return 'locations';
      case 'admin-buildings': return 'buildings';
      case 'admin-floors': return 'floors';
      case 'admin-departments': return 'departments';
      case 'admin-rooms': return 'rooms';
      case 'admin-beds': return 'beds';
      case 'admin-devices': return 'equipment';
      default: return 'organizations';
    }
  };

  // Clear selected item when switching entity types (only when activeLevel actually changes)
  useEffect(() => {
    if (selectedItem) {
      const currentEntityType = getCurrentEntityType();
      // Only clear if we're actually switching to a different entity type
      if (selectedItem.type && selectedItem.type !== currentEntityType) {
        console.log('Clearing selectedItem due to entity type mismatch:', {
          selectedItemType: selectedItem.type,
          currentEntityType: currentEntityType
        });
        setSelectedItem(null);
      }
    }
  }, [activeLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle expand/collapse
  const toggleExpand = (type, id) => {
    const key = `${type}-${id}`;
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isExpanded = (type, id) => searchQuery ? true : expandedItems[`${type}-${id}`];

  // CRUD Operations
  const addItem = (type, parentId = null, additionalData = {}) => {
    const newItem = {
      id: generateId(),
      type: type, // Track which entity type this belongs to
      name: `New ${type.slice(0, -1)}`,
      ...additionalData
    };

    // Map parent relationships correctly for data aggregation
    if (type === 'locations' && organizationId) {
      newItem.organizationId = organizationId;
    } else if (parentId) {
      if (type === 'locations') {
        newItem.organizationId = parentId;
      } else if (type === 'buildings') {
        newItem.locationId = parentId;
      } else if (type === 'floors') {
        newItem.buildingId = parentId;
      } else if (type === 'departments') {
        newItem.floorId = parentId;
      } else if (type === 'rooms') {
        newItem.departmentId = parentId;
      } else if (type === 'beds') {
        newItem.roomId = parentId;
      } else if (type === 'equipment') {
        newItem.departmentId = parentId;
      } else if (type === 'personnel') {
        newItem.departmentId = parentId;
      }
    }

    setStructure(prev => ({
      ...prev,
      [type]: [...(prev[type] || []), newItem]
    }));

    // Audit log
    auditService.logCreate(type, newItem.id, newItem.name, {
      parentId,
      ...additionalData
    });

    setSelectedItem(newItem);
    setPendingNewItem({ type, id: newItem.id });
    return newItem.id;
  };

  const updateItem = (type, id, updates) => {
    console.log('updateItem called:', { type, id, updates });
    console.log('Current selectedItem:', selectedItem);
    
    // Update the structure first
    setStructure(prev => ({
      ...prev,
      [type]: (prev[type] || []).map(item => item.id === id ? { ...item, ...updates } : item)
    }));
    
    // Only update selected item if it's the same item AND same entity type
    // Also make sure we don't clear the selectedItem unnecessarily
    if (selectedItem && selectedItem.id === id && selectedItem.type === type) {
      console.log('Updating selectedItem with:', updates);
      // Use a more careful update that preserves the selectedItem state
      setSelectedItem(prev => {
        const updated = { ...prev, ...updates };
        console.log('Updated selectedItem:', updated);
        return updated;
      });
    } else {
      console.log('Not updating selectedItem - conditions not met:', {
        hasSelectedItem: !!selectedItem,
        idMatch: selectedItem?.id === id,
        entityTypeMatch: selectedItem?.entityType === type
      });
    }
  };

  const deleteItem = (type, id) => {
    // Get the item name for audit log before deleting
    const item = structure[type]?.find(i => i.id === id);
    const itemName = item?.name || 'Unknown';
    
    setStructure(prev => ({
      ...prev,
      [type]: (prev[type] || []).filter(item => item.id !== id)
    }));
    
    // Audit log
    auditService.logDelete(type, id, itemName);
  };

  // Required field keys per entity type (must match field config required: true)
  const REQUIRED_FIELD_KEYS = {
    organizations: ['name'],
    locations: ['name', 'organizationId'],
    buildings: ['name', 'locationId'],
    floors: ['name', 'buildingId'],
    departments: ['name', 'floorId'],
    rooms: ['name', 'departmentId'],
    beds: ['bedNumber', 'roomId'],
    equipment: ['name', 'departmentId', 'status'],
    personnel: ['name'],
    biological_entities: ['name']
  };

  const validateRequiredFields = (type, item) => {
    const keys = REQUIRED_FIELD_KEYS[type];
    if (!keys || keys.length === 0) return true;
    for (const key of keys) {
      const value = item[key];
      if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) return false;
    }
    return true;
  };

  // Helper functions for nested object fields
  const getNestedValue = (item, key) => {
    if (!item || !key) return '';
    if (!key.includes('.')) return item[key] || '';
    const keys = key.split('.');
    let value = item;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || '';
  };

  const updateNestedItem = (type, id, key, value) => {
    if (!key.includes('.')) {
      updateItem(type, id, { [key]: value });
      return;
    }
    
    const keys = key.split('.');
    
    setStructure(prev => ({
      ...prev,
      [type]: (prev[type] || []).map(item => {
        if (item.id === id) {
          const newItem = { ...item };
          let target = newItem;
          for (let i = 0; i < keys.length - 1; i++) {
            if (!target[keys[i]]) target[keys[i]] = {};
            target = target[keys[i]];
          }
          target[keys[keys.length - 1]] = value;
          return newItem;
        }
        return item;
      })
    }));
    
    // Only update selected item if it's the same item AND same entity type
    if (selectedItem && selectedItem.id === id && selectedItem.type === type) {
      const newSelectedItem = { ...selectedItem };
      let target = newSelectedItem;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!target[keys[i]]) target[keys[i]] = {};
        target = target[keys[i]];
      }
      target[keys[keys.length - 1]] = value;
      setSelectedItem(newSelectedItem);
    }
  };

  const getEntityDisplayName = (type) => {
    switch(type) {
      case 'organizations': return t('entityHospitals');
      case 'locations': return t('entityLocations');
      case 'buildings': return t('entityBuildings');
      case 'floors': return t('entityFloors');
      case 'departments': return t('entityDepartments');
      case 'rooms': return t('entityRooms');
      case 'beds': return t('entityBeds');
      case 'equipment': return t('entityDevices');
      default: return t('entityItems');
    }
  };

  /** Singular form for edit title (e.g. "Edit bed" / "Editează pat") */
  const getEntityDisplayNameSingular = (type) => {
    if (type === 'beds') return t('entityBed');
    const plural = getEntityDisplayName(type);
    return plural.slice(0, -1);
  };

  // Render form fields based on entity type
  const renderFormFields = (type, item) => {
    if (!item) return null;

    const fields = {
      organizations: [
        { key: 'name', labelKey: 'fieldHospitalName', label: 'Hospital Name', type: 'text', required: true },
        { key: 'address', labelKey: 'fieldAddress', label: 'Address', type: 'text' },
        { key: 'phone', labelKey: 'fieldPhone', label: 'Phone', type: 'text' },
        { key: 'email', labelKey: 'fieldEmail', label: 'Email', type: 'email' },
        { key: 'website', labelKey: 'fieldWebsite', label: 'Website', type: 'url' },
        { key: 'officialRep', labelKey: 'fieldOfficialRep', label: 'Official Representative', type: 'text' },
        { key: 'medicalRep', labelKey: 'fieldMedicalRep', label: 'Medical Representative', type: 'text' },
        { key: 'entityType', labelKey: 'fieldEntityType', label: 'Entity Type', type: 'select', options: ['spital-universitar', 'spital-clinic', 'spital-municipal', 'spital-județean', 'General', 'Specialized', 'Teaching', 'Research'] },
        { key: 'totalBeds', labelKey: 'fieldTotalBeds', label: 'Total Beds', type: 'number' },
        { key: 'established', labelKey: 'fieldEstablished', label: 'Established', type: 'text' }
      ],
      locations: [
        { key: 'name', labelKey: 'fieldLocationName', label: 'Location Name', type: 'text', required: true },
        { key: 'organizationId', labelKey: 'fieldParentHospital', label: 'Parent Hospital', type: 'parent', parentType: 'organizations', required: true },
        { key: 'address', labelKey: 'fieldAddress', label: 'Address', type: 'text' },
        { key: 'contactPerson', labelKey: 'fieldContactPerson', label: 'Contact Person', type: 'text' },
        { key: 'phone', labelKey: 'fieldPhone', label: 'Phone', type: 'text' },
        { key: 'email', labelKey: 'fieldEmail', label: 'Email', type: 'email' },
        { key: 'status', labelKey: 'fieldStatus', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        { key: 'emergencyServices', labelKey: 'fieldEmergencyServices', label: 'Emergency Services', type: 'select', options: ['Yes', 'No'] }
      ],
      buildings: [
        { key: 'name', labelKey: 'fieldBuildingName', label: 'Building Name', type: 'text', required: true },
        { key: 'locationId', labelKey: 'fieldParentLocation', label: 'Parent Location', type: 'parent', parentType: 'locations', required: true },
        { key: 'address', labelKey: 'fieldAddress', label: 'Address', type: 'text' },
        { key: 'floors', labelKey: 'fieldFloors', label: 'Number of Floors', type: 'number' },
        { key: 'yearBuilt', labelKey: 'fieldYearBuilt', label: 'Year Built', type: 'number' },
        { key: 'lastRenovated', labelKey: 'fieldLastRenovated', label: 'Last Renovated', type: 'number' },
        { key: 'status', labelKey: 'fieldStatus', label: 'Status', type: 'select', options: ['Active', 'Inactive'] }
      ],
      floors: [
        { key: 'name', labelKey: 'fieldFloorName', label: 'Floor Name', type: 'text', required: true },
        { key: 'buildingId', labelKey: 'fieldParentBuilding', label: 'Parent Building', type: 'parent', parentType: 'buildings', required: true },
        { key: 'number', labelKey: 'fieldNumber', label: 'Floor Number', type: 'text' },
        { key: 'status', labelKey: 'fieldStatus', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        { key: 'description', labelKey: 'fieldDescription', label: 'Description', type: 'textarea' },
        { key: 'clinicalZoneType', labelKey: 'fieldClinicalZoneType', label: 'Clinical Zone Type', type: 'select', options: ['General Care', 'ICU', 'Surgery', 'Emergency'] }
      ],
      departments: [
        { key: 'name', labelKey: 'fieldDepartmentName', label: 'Department Name', type: 'text', required: true },
        { key: 'floorId', labelKey: 'fieldParentFloor', label: 'Parent Floor', type: 'parent', parentType: 'floors', required: true },
        { key: 'departmentType', labelKey: 'fieldDepartmentType', label: 'Department Type', type: 'text' },
        { key: 'shortName', labelKey: 'fieldShortName', label: 'Short Name', type: 'text' },
        { key: 'medicalSpecialty', labelKey: 'fieldMedicalSpecialty', label: 'Medical Specialty', type: 'text' },
        { key: 'status', labelKey: 'fieldStatus', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        { key: 'headDoctor', labelKey: 'fieldHeadDoctor', label: 'Head Doctor', type: 'text' },
        { key: 'headNurse', labelKey: 'fieldHeadNurse', label: 'Head Nurse', type: 'text' },
        { key: 'assignedPersonnel', labelKey: 'fieldAssignedPersonnel', label: 'Assigned Personnel', type: 'readonly' },
        { key: 'riskClassificationCard', label: 'Risk classification', type: 'riskClassificationCard' }
      ],
      rooms: [
        { key: 'name', labelKey: 'fieldRoomName', label: 'Room Name', type: 'text', required: true },
        { key: 'departmentId', labelKey: 'fieldParentDepartment', label: 'Parent Department', type: 'parent', parentType: 'departments', required: true },
        { key: 'roomNumber', labelKey: 'fieldRoomNumber', label: 'Room Number', type: 'text' },
        { key: 'roomType', labelKey: 'fieldRoomType', label: 'Room Type', type: 'nomenclature', nomenclatureType: 'roomTypes' },
        { key: 'capacity', labelKey: 'fieldCapacity', label: 'Capacity', type: 'number' },
        { key: 'status', labelKey: 'fieldStatus', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
        { key: 'riskClassificationCard', label: 'Risk classification', type: 'riskClassificationCard' },
        { key: 'airFiltrationLevel', labelKey: 'fieldAirFiltrationLevel', label: 'Air Filtration Level', type: 'select', options: ['Standard', 'HEPA', 'Negative Pressure', 'Positive Pressure'] },
        { key: 'surfaceMaterials', labelKey: 'fieldSurfaceMaterials', label: 'Surface Materials', type: 'text' }
      ],
      beds: [
        { key: 'bedNumber', labelKey: 'fieldBedNumber', label: 'Bed Number', type: 'text', required: true },
        { key: 'roomId', labelKey: 'fieldParentRoom', label: 'Parent Room', type: 'parent', parentType: 'rooms', required: true },
        { key: 'riskClassificationCard', label: 'Risk classification', type: 'riskClassificationCard' },
        { key: 'bedType', labelKey: 'fieldBedType', label: 'Bed Type', type: 'select', options: ['Medical', 'Surgical', 'ICU', 'Pediatric', 'Bariatric'] }
      ],
      equipment: [
        { key: 'name', labelKey: 'fieldEquipmentName', label: 'Equipment Name', type: 'text', required: true },
        { key: 'departmentId', labelKey: 'fieldParentDepartment', label: 'Parent Department', type: 'parent', parentType: 'departments', required: true },
        { key: 'status', labelKey: 'fieldStatus', label: 'Status', type: 'select', options: ['Operational', 'Maintenance', 'Out of Service'], required: true },
        { key: 'serialNumber', labelKey: 'fieldSerialNumber', label: 'Serial Number', type: 'text' },
        { key: 'manufacturer', labelKey: 'fieldManufacturer', label: 'Manufacturer', type: 'text' },
        { key: 'lastMaintenance', labelKey: 'fieldLastMaintenance', label: 'Last Maintenance', type: 'date' },
        { key: 'nextMaintenance', labelKey: 'fieldNextMaintenance', label: 'Next Maintenance', type: 'date' },
        { key: 'calibrationDue', labelKey: 'fieldCalibrationDue', label: 'Calibration Due', type: 'date' },
        { key: 'purchaseDate', labelKey: 'fieldPurchaseDate', label: 'Purchase Date', type: 'date' },
        { key: 'warrantyExpiry', labelKey: 'fieldWarrantyExpiry', label: 'Warranty Expiry', type: 'date' },
        { key: 'riskClassificationCard', label: 'Risk classification', type: 'riskClassificationCard' }
      ],
      personnel: [
        { key: 'name', labelKey: 'fieldFullName', label: 'Full Name', type: 'text', required: true },
        { key: 'position', labelKey: 'fieldPosition', label: 'Position', type: 'text' },
        { key: 'department', labelKey: 'fieldDepartment', label: 'Department', type: 'text' },
        { key: 'role', labelKey: 'fieldRole', label: 'Role', type: 'select', options: ['Doctor', 'Nurse', 'Administrator', 'Technician', 'Support Staff'] },
        { key: 'email', labelKey: 'fieldEmail', label: 'Email', type: 'email' },
        { key: 'phone', labelKey: 'fieldPhone', label: 'Phone', type: 'text' },
        { key: 'employeeId', labelKey: 'fieldEmployeeId', label: 'Employee ID', type: 'text' },
        { key: 'hireDate', labelKey: 'fieldHireDate', label: 'Hire Date', type: 'date' },
        { key: 'organizationId', labelKey: 'fieldOrganizationId', label: 'Organization ID', type: 'text' },
        { key: 'departmentId', labelKey: 'fieldDepartmentId', label: 'Department ID', type: 'text' },
        { key: 'status', labelKey: 'fieldStatus', label: 'Status', type: 'select', options: ['Active', 'Inactive', 'On Leave'] }
      ],
      biological_entities: [
        { key: 'name', label: 'Entity Name', type: 'text', required: true },
        { key: 'type', label: 'Entity Type', type: 'select', options: ['Bacteria', 'Virus', 'Fungus', 'Parasite', 'Pathogen'] },
        { key: 'classification', label: 'Classification', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'riskLevel', label: 'Risk Level', type: 'select', options: ['Low', 'Medium', 'High', 'Critical'] },
        { key: 'transmissionMethod', label: 'Transmission Method', type: 'text' },
        { key: 'incubationPeriod', label: 'Incubation Period', type: 'text' },
        { key: 'symptoms', label: 'Symptoms', type: 'textarea' },
        { key: 'treatment', label: 'Treatment', type: 'textarea' },
        { key: 'prevention', label: 'Prevention', type: 'textarea' },
        { key: 'organizationId', label: 'Organization ID', type: 'text' },
        { key: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive', 'Quarantined'] }
      ]
    };

    const typeFields = fields[type] || [];

    return (
      <div className="form-grid">
        {typeFields.map(field => (
          <div key={field.key} className={`form-field ${field.type === 'textarea' || field.type === 'riskClassificationCard' ? 'full-width' : ''}`}>
            {field.type === 'riskClassificationCard' ? (
              !orgLoading && organizationId && (() => {
                const entityTypeMap = { departments: 'department', rooms: 'room', beds: 'bed', equipment: 'equipment' };
                const assignmentEntityType = entityTypeMap[type];
                const currentItem = structure[type]?.find(i => i.id === item.id) || item;
                return (
                  <RiskClassificationAssignmentPanel
                    organizationId={organizationId}
                    entityType={assignmentEntityType}
                    entityId={currentItem?.id}
                    structure={structure}
                    item={currentItem}
                    userId={userId}
                    useModal
                  />
                );
              })()
            ) : (
              <>
            <label>
              {(field.labelKey ? t(field.labelKey) : field.label)}
              {field.required && <span className="required">*</span>}
            </label>
            {field.type === 'parent' ? (
              <select
                value={item[field.key] || ''}
                onChange={(e) => updateItem(type, item.id, { [field.key]: e.target.value })}
                required={field.required}
              >
                <option value="">{t('selectField', { label: field.labelKey ? t(field.labelKey) : field.label })}</option>
                {(structure[field.parentType] || []).map(parentItem => (
                  <option key={parentItem.id} value={parentItem.id}>
                    {parentItem.name || t('unnamed')}
                  </option>
                ))}
              </select>
            ) : field.type === 'nomenclature' ? (
              roomTypesLoading ? (
                <div className="loading-indicator">{t('loadingRoomTypes')}</div>
              ) : !roomTypes || roomTypes.length === 0 ? (
                <div style={{ padding: '8px', color: '#e53e3e', fontSize: '13px' }}>
                  ⚠️ {t('noRoomTypesFound')}
                </div>
              ) : (
                <select
                  value={item[field.key] || ''}
                  onChange={(e) => updateItem(type, item.id, { [field.key]: e.target.value })}
                  required={field.required}
                >
                  <option value="">{t('selectField', { label: field.labelKey ? t(field.labelKey) : field.label })}</option>
                  {roomTypes.map(rt => (
                    <option key={rt.id} value={rt.name}>
                      {rt.name} ({rt.code})
                    </option>
                  ))}
                </select>
              )
            ) : field.type === 'select' ? (
              <select
                value={item[field.key] || ''}
                onChange={(e) => {
                  console.log('Select onChange triggered:', {
                    field: field.key,
                    value: e.target.value,
                    item: item,
                    type: type
                  });
                  updateItem(type, item.id, { [field.key]: e.target.value });
                }}
                required={field.required}
              >
                <option value="">{t('selectField', { label: field.labelKey ? t(field.labelKey) : field.label })}</option>
                {field.options?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : field.type === 'textarea' ? (
              <textarea
                value={item[field.key] || ''}
                onChange={(e) => updateItem(type, item.id, { [field.key]: e.target.value })}
                required={field.required}
                rows={3}
              />
            ) : field.type === 'readonly' && field.key === 'assignedPersonnel' ? (
              <div className="personnel-display">
                <div className="personnel-header">
                  <strong>
                    {t('usersAssignedCount', { count: structure.users ? structure.users.filter(u => u.primary_department_id === item.id).length : 0 })}
                  </strong>
                </div>
                {structure.users && structure.users.filter(u => u.primary_department_id === item.id).length > 0 ? (
                  <div className="personnel-table">
                    <div className="personnel-table-header">
                      <div className="col-name">Name</div>
                      <div className="col-role">Role</div>
                      <div className="col-email">Email</div>
                      <div className="col-status">Status</div>
                    </div>
                    <div className="personnel-table-body">
                      {structure.users
                        .filter(u => u.primary_department_id === item.id)
                        .map((user, index) => (
                          <div key={user.id || index} className="personnel-row">
                            <div className="col-name">
                              {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || t('unnamedUser')}
                            </div>
                            <div className="col-role">
                              {user.role_id || user.specialty || t('staff')}
                            </div>
                            <div className="col-email">
                              {user.email}
                            </div>
                            <div className="col-status">
                              <span className={`status-badge ${user.status || 'active'}`}>
                                {user.status || t('active')}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-personnel">
                    <p>{t('noUsersAssigned')}</p>
                    <p><small>{t('noUsersAssignedHint')}</small></p>
                  </div>
                )}
              </div>
            ) : (
              <input
                type={field.type}
                value={item[field.key] || ''}
                onChange={(e) => updateItem(type, item.id, { [field.key]: e.target.value })}
                required={field.required}
              />
            )}
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Filter items based on search query
  const filterItems = (items, query) => {
    if (!query) return items;
    return items.filter(item => 
      item.name?.toLowerCase().includes(query.toLowerCase()) ||
      item.address?.toLowerCase().includes(query.toLowerCase()) ||
      item.type?.toLowerCase().includes(query.toLowerCase())
    );
  };

  // Generate breadcrumb path for an entity
  const getEntityBreadcrumb = (item, entityType) => {
    const breadcrumbs = [];
    
    switch (entityType) {
      case 'organizations':
        breadcrumbs.push(item.name || t('unnamed'));
        break;
        
      case 'locations':
        breadcrumbs.push(item.name || t('unnamed'));
        if (item.organizationId) {
          const org = structure.organizations?.find(o => o.id === item.organizationId);
          if (org) breadcrumbs.unshift(org.name || t('unnamed'));
        }
        break;
        
      case 'buildings':
        breadcrumbs.push(item.name || t('unnamed'));
        if (item.locationId) {
          const location = structure.locations?.find(l => l.id === item.locationId);
          if (location) {
            breadcrumbs.unshift(location.name || t('unnamed'));
            if (location.organizationId) {
              const org = structure.organizations?.find(o => o.id === location.organizationId);
              if (org) breadcrumbs.unshift(org.name || t('unnamed'));
            }
          }
        }
        break;
        
      case 'floors':
        breadcrumbs.push(item.name || t('unnamed'));
        if (item.buildingId) {
          const building = structure.buildings?.find(b => b.id === item.buildingId);
          if (building) {
            breadcrumbs.unshift(building.name || t('unnamed'));
            if (building.locationId) {
              const location = structure.locations?.find(l => l.id === building.locationId);
              if (location) {
                breadcrumbs.unshift(location.name || t('unnamed'));
                if (location.organizationId) {
                  const org = structure.organizations?.find(o => o.id === location.organizationId);
                  if (org) breadcrumbs.unshift(org.name || t('unnamed'));
                }
              }
            }
          }
        }
        break;
        
      case 'departments':
        breadcrumbs.push(item.name || t('unnamed'));
        if (item.floorId) {
          const floor = structure.floors?.find(f => f.id === item.floorId);
          if (floor) {
            breadcrumbs.unshift(floor.name || t('unnamed'));
            if (floor.buildingId) {
              const building = structure.buildings?.find(b => b.id === floor.buildingId);
              if (building) {
                breadcrumbs.unshift(building.name || t('unnamed'));
                if (building.locationId) {
                  const location = structure.locations?.find(l => l.id === building.locationId);
                  if (location) {
                    breadcrumbs.unshift(location.name || t('unnamed'));
                    if (location.organizationId) {
                      const org = structure.organizations?.find(o => o.id === location.organizationId);
                      if (org) breadcrumbs.unshift(org.name || t('unnamed'));
                    }
                  }
                }
              }
            }
          }
        }
        break;
        
      case 'rooms':
        breadcrumbs.push(item.name || t('unnamed'));
        if (item.departmentId) {
          const department = structure.departments?.find(d => d.id === item.departmentId);
          if (department) {
            breadcrumbs.unshift(department.name || t('unnamed'));
            if (department.floorId) {
              const floor = structure.floors?.find(f => f.id === department.floorId);
              if (floor) {
                breadcrumbs.unshift(floor.name || t('unnamed'));
                if (floor.buildingId) {
                  const building = structure.buildings?.find(b => b.id === floor.buildingId);
                  if (building) {
                    breadcrumbs.unshift(building.name || t('unnamed'));
                    if (building.locationId) {
                      const location = structure.locations?.find(l => l.id === building.locationId);
                      if (location) {
                        breadcrumbs.unshift(location.name || t('unnamed'));
                        if (location.organizationId) {
                          const org = structure.organizations?.find(o => o.id === location.organizationId);
                          if (org) breadcrumbs.unshift(org.name || t('unnamed'));
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        break;
        
      case 'beds':
        // Special case for beds - show "Bed: [number]" format
        const bedNumber = item.bedNumber || item.name || 'Unknown';
        breadcrumbs.push(`Bed: ${bedNumber}`);
        if (item.roomId) {
          const room = structure.rooms?.find(r => r.id === item.roomId);
          if (room) {
            breadcrumbs.unshift(room.name || t('unnamed'));
            if (room.departmentId) {
              const department = structure.departments?.find(d => d.id === room.departmentId);
              if (department) {
                breadcrumbs.unshift(department.name || t('unnamed'));
                if (department.floorId) {
                  const floor = structure.floors?.find(f => f.id === department.floorId);
                  if (floor) {
                    breadcrumbs.unshift(floor.name || t('unnamed'));
                    if (floor.buildingId) {
                      const building = structure.buildings?.find(b => b.id === floor.buildingId);
                      if (building) {
                        breadcrumbs.unshift(building.name || t('unnamed'));
                        if (building.locationId) {
                          const location = structure.locations?.find(l => l.id === building.locationId);
                          if (location) {
                            breadcrumbs.unshift(location.name || t('unnamed'));
                            if (location.organizationId) {
                              const org = structure.organizations?.find(o => o.id === location.organizationId);
                              if (org) breadcrumbs.unshift(org.name || t('unnamed'));
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        break;
        
      case 'equipment':
        breadcrumbs.push(item.name || t('unnamed'));
        if (item.departmentId) {
          const department = structure.departments?.find(d => d.id === item.departmentId);
          if (department) {
            breadcrumbs.unshift(department.name || t('unnamed'));
            if (department.floorId) {
              const floor = structure.floors?.find(f => f.id === department.floorId);
              if (floor) {
                breadcrumbs.unshift(floor.name || t('unnamed'));
                if (floor.buildingId) {
                  const building = structure.buildings?.find(b => b.id === floor.buildingId);
                  if (building) {
                    breadcrumbs.unshift(building.name || t('unnamed'));
                    if (building.locationId) {
                      const location = structure.locations?.find(l => l.id === building.locationId);
                      if (location) {
                        breadcrumbs.unshift(location.name || t('unnamed'));
                        if (location.organizationId) {
                          const org = structure.organizations?.find(o => o.id === location.organizationId);
                          if (org) breadcrumbs.unshift(org.name || t('unnamed'));
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        break;
    }
    
    return breadcrumbs.join(' → ');
  };

  if (!structure) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('loadingStructure')}</p>
      </div>
    );
  }

  const currentEntityType = getCurrentEntityType();
  const currentItems = structure[currentEntityType] || [];
  const filteredItems = filterItems(currentItems, searchQuery);

  return (
    <div style={{ padding: '24px' }}>
      {/* Search Bar */}
      <div className="content-card" style={{ marginBottom: '24px' }}>
        <div className="card-content">
          <div className="form-field">
            <label>Search {getEntityDisplayName(currentEntityType)}</label>
            <input
              type="text"
              placeholder={`Search ${getEntityDisplayName(currentEntityType).toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Entity List */}
      <div className="content-card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <div className="card-title">
            {getEntityDisplayName(currentEntityType)} ({filteredItems.length})
          </div>
          <button onClick={() => addItem(currentEntityType)} className="btn btn-primary">
            {t('addEntity', { entity: getEntityDisplayName(currentEntityType).slice(0, -1) })}
          </button>
        </div>
        <div className="card-content">
          <div className="entity-list">
            {filteredItems.map(item => (
              <React.Fragment key={item.id}>
                <div
                  className={`entity-item ${selectedItem?.id === item.id ? 'selected' : ''}`}
                  onClick={() => setSelectedItem({ ...item, type: currentEntityType })}
                >
                <div className="entity-info">
                  <div className="entity-name">
                      {currentEntityType === 'beds'
                        ? (item.name || item.bedNumber || t('unnamed'))
                        : (item.name || t('unnamed'))}
                    </div>
                  <div className="entity-breadcrumb" style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginBottom: '8px',
                    fontFamily: 'monospace'
                  }}>
                    {getEntityBreadcrumb(item, currentEntityType)}
                  </div>
                  <div className="entity-details">
                    {item.address && <span>📍 {item.address}</span>}
                    {item.type && <span>🏷️ {item.type}</span>}
                    {item.status && <span className={`status ${item.status?.toLowerCase()}`}>{item.status}</span>}
                  </div>
                </div>
                <div className="entity-actions">
                  <button 
                    className="btn btn-edit btn-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedItem({ ...item, entityType: currentEntityType });
                    }}
                    title="Edit Item"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    className="btn btn-delete btn-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(t('confirmDelete'))) {
                        deleteItem(currentEntityType, item.id);
                        if (selectedItem?.id === item.id) {
                          setSelectedItem(null);
                        }
                      }
                    }}
                    title={t('deleteItem')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
                {selectedItem?.id === item.id && selectedItem?.type === currentEntityType && (
                  <div className="content-card entity-detail-inline">
                    <div className="card-header">
                      <div className="card-title">
                        {t('editEntity', { entity: getEntityDisplayNameSingular(currentEntityType) })}
                      </div>
                    </div>
                    <div className="card-content">
                      {renderFormFields(currentEntityType, selectedItem)}
                      <div className="form-actions">
                        {(() => {
                          const currentItem = structure[currentEntityType]?.find(i => i.id === selectedItem.id) || selectedItem;
                          const canSave = validateRequiredFields(currentEntityType, currentItem);
                          return (
                            <button
                              className={`btn btn-primary ${saveSuccess ? 'save-success' : ''}`}
                              disabled={!canSave}
                              title={!canSave ? t('fillRequiredHint') : ''}
                              onClick={() => {
                                const it = structure[currentEntityType]?.find(i => i.id === selectedItem.id) || selectedItem;
                                if (!validateRequiredFields(currentEntityType, it)) return;
                                auditService.logUpdate(currentEntityType, it.id, it.name || it.bedNumber || t('unnamed'), { action: 'Saved changes' });
                                setPendingNewItem(null);
                                setSaveSuccess(true);
                                setTimeout(() => setSaveSuccess(false), 2000);
                              }}
                            >
                              {saveSuccess ? (
                                <>
                                  <span className="checkmark">✓</span> {t('saved')}
                                </>
                              ) : (
                                'Save Changes'
                              )}
                            </button>
                          );
                        })()}
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            const type = getCurrentEntityType();
                            if (pendingNewItem && selectedItem?.id === pendingNewItem.id && type === pendingNewItem.type) {
                              deleteItem(type, selectedItem.id);
                              setPendingNewItem(null);
                            }
                            setSelectedItem(null);
                          }}
                        >
                          {t('cancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
            {filteredItems.length === 0 && (
              <div className="empty-state">
                <p>{t('noEntityFound', { entity: getEntityDisplayName(currentEntityType) })}</p>
                <button onClick={() => addItem(currentEntityType)} className="btn btn-primary">
                  {t('addFirstEntity', { entity: getEntityDisplayName(currentEntityType).slice(0, -1) })}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicalMapper;